// 권위: 화면 E TradingBacktestResults 의 fallback 데이터.
// dashboard-api 의 backtest endpoint 가 부재할 때 노출되는 mock 만 정의한다.
// 실 API 응답 schema 는 dashboard-api 측에서 확정 후 본 타입과 정합 맞춤.

export interface BacktestRunSummary {
  id: string;
  label?: string;
  startDate: string;
  endDate: string;
  finalCapital: number;
  cagr: number; // 0.123 = 12.3%
  sharpe: number;
  mdd: number; // 0.084 = -8.4% 절댓값
  winRate: number; // 0.55 = 55%
  tradingDays: number;
  createdAt: string;
}

export interface BacktestEquityPoint {
  date: string;
  equity: number;
}

export interface BacktestSignalBreakdown {
  kind: 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT';
  generated: number;
  queued: number;
  executed: number;
  dropped: number;
}

export interface BacktestRunDetail extends BacktestRunSummary {
  equityCurve: BacktestEquityPoint[];
  signalBreakdown: BacktestSignalBreakdown[];
}

export interface BacktestAuditItem {
  occurredAt: string;
  symbol: string;
  eventCode: string;
  summary: string;
}

export interface BacktestAuditPage {
  items: BacktestAuditItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ── 거래내역 (trades) ─────────────────────────────────────────
// 사용자 통찰 (보고서 §7): Top 5 KRW PnL 정렬은 우량주 cohort (시총 큰 종목)를
// 누락한다. % return 정렬을 기본 옵션으로 제공하고, 화면 footer 에 짧게 언급한다.
//
// payload 는 dashboard-api 가 병렬로 구현 중인 GET /api/v1/backtest/runs/{id}/trades
// 응답 스키마와 정합 — symbolName 은 phase 2 에서 추가됨 (api 응답에 없을 수도 있어
// optional).

export interface BacktestTradeCycle {
  cycleId: string;
  symbol: string;
  symbolName?: string | null;
  sector: string;
  openedAt: string;       // ISO-8601
  closedAt: string | null; // null = 미청산
  entryPrice: number;
  avgExitPrice: number | null;
  qty: number;
  cost: number;
  realizedPnl: number;    // KRW (수수료/세금 포함 net_pnl)
  returnPct: number;      // 78.5 = 78.5%
  holdDays: number;
  exitCascade: string[];  // ex: ["tp_15pct", "tp_25pct", "ma20_exit"]
  closeCount: number;
}

export interface BacktestTradesPage {
  runId: string;
  totalCycles: number;
  trades: BacktestTradeCycle[];
}

// ── Mock 데이터 ───────────────────────────────────────────────

export const MOCK_BACKTEST_RUNS: BacktestRunSummary[] = [
  {
    id: 'bt-2026-05-01',
    label: 'baseline_v240',
    startDate: '2024-01-02',
    endDate: '2026-04-30',
    finalCapital: 138_240_000,
    cagr: 0.157,
    sharpe: 1.42,
    mdd: 0.084,
    winRate: 0.58,
    tradingDays: 568,
    createdAt: '2026-05-01T03:14:00+09:00',
  },
  {
    id: 'bt-2026-04-15',
    label: 'leader_v110_strict',
    startDate: '2024-01-02',
    endDate: '2026-04-12',
    finalCapital: 119_400_000,
    cagr: 0.092,
    sharpe: 1.05,
    mdd: 0.122,
    winRate: 0.51,
    tradingDays: 555,
    createdAt: '2026-04-15T11:20:00+09:00',
  },
  {
    id: 'bt-2026-04-02',
    label: 'sector_rotation_alpha',
    startDate: '2024-06-01',
    endDate: '2026-03-31',
    finalCapital: 142_800_000,
    cagr: 0.198,
    sharpe: 1.61,
    mdd: 0.071,
    winRate: 0.62,
    tradingDays: 458,
    createdAt: '2026-04-02T22:00:00+09:00',
  },
];

const SAMPLE_EQUITY: BacktestEquityPoint[] = (() => {
  const out: BacktestEquityPoint[] = [];
  let v = 100_000_000;
  const start = new Date('2024-01-02T00:00:00Z');
  for (let i = 0; i < 60; i += 1) {
    const d = new Date(start.getTime() + i * 30 * 86400_000);
    const drift = 1.012 + (i % 7 === 0 ? -0.018 : 0.001 * (i % 5));
    v = Math.round(v * drift);
    out.push({ date: d.toISOString().slice(0, 10), equity: v });
  }
  return out;
})();

const SAMPLE_BREAKDOWN: BacktestSignalBreakdown[] = [
  { kind: 'BUY', generated: 320, queued: 280, executed: 240, dropped: 40 },
  { kind: 'SELL', generated: 210, queued: 198, executed: 188, dropped: 12 },
  { kind: 'STOP_LOSS', generated: 64, queued: 64, executed: 62, dropped: 2 },
  { kind: 'TAKE_PROFIT', generated: 102, queued: 100, executed: 98, dropped: 2 },
];

export const MOCK_BACKTEST_DETAILS: Record<string, BacktestRunDetail> = {
  __default__: {
    ...MOCK_BACKTEST_RUNS[0],
    equityCurve: SAMPLE_EQUITY,
    signalBreakdown: SAMPLE_BREAKDOWN,
  },
  'bt-2026-05-01': {
    ...MOCK_BACKTEST_RUNS[0],
    equityCurve: SAMPLE_EQUITY,
    signalBreakdown: SAMPLE_BREAKDOWN,
  },
  'bt-2026-04-15': {
    ...MOCK_BACKTEST_RUNS[1],
    equityCurve: SAMPLE_EQUITY.map((p, i) => ({
      date: p.date,
      equity: Math.round(p.equity * (0.92 + 0.001 * i)),
    })),
    signalBreakdown: SAMPLE_BREAKDOWN.map((b) => ({
      ...b,
      executed: Math.round(b.executed * 0.85),
    })),
  },
  'bt-2026-04-02': {
    ...MOCK_BACKTEST_RUNS[2],
    equityCurve: SAMPLE_EQUITY.map((p, i) => ({
      date: p.date,
      equity: Math.round(p.equity * (1.02 + 0.0008 * i)),
    })),
    signalBreakdown: SAMPLE_BREAKDOWN.map((b) => ({
      ...b,
      executed: Math.round(b.executed * 1.05),
    })),
  },
};

// 거래내역 mock — 다양한 sector + exit cascade + 우량주/소형주 mix.
// 정렬 우선순위 가시화 목적 (KRW PnL 최대값 = 우량주 cohort 누락 시연 포함).
const SAMPLE_TRADES: BacktestTradeCycle[] = [
  {
    cycleId: '097230-1721880000-BT-01',
    symbol: '097230',
    symbolName: 'HJ중공업',
    sector: '방산·우주항공',
    openedAt: '2025-07-25T15:20:00+09:00',
    closedAt: '2025-10-04T15:00:00+09:00',
    entryPrice: 8442.53,
    avgExitPrice: 11500.30,
    qty: 567,
    cost: 4_786_915.64,
    realizedPnl: 3_760_000,
    returnPct: 78.5,
    holdDays: 71,
    exitCascade: ['tp_15pct', 'tp_25pct', 'ma20_exit'],
    closeCount: 3,
  },
  {
    cycleId: '005930-1718000000-BT-02',
    symbol: '005930',
    symbolName: '삼성전자',
    sector: '반도체',
    openedAt: '2025-06-10T09:30:00+09:00',
    closedAt: '2025-08-22T13:14:00+09:00',
    entryPrice: 71_200,
    avgExitPrice: 78_400,
    qty: 140,
    cost: 9_968_000,
    realizedPnl: 1_008_000,
    returnPct: 10.1,
    holdDays: 73,
    exitCascade: ['tp_10pct', 'ma20_exit'],
    closeCount: 2,
  },
  {
    cycleId: '247540-1719000000-BT-03',
    symbol: '247540',
    symbolName: '에코프로비엠',
    sector: '2차전지',
    openedAt: '2025-06-22T10:00:00+09:00',
    closedAt: '2025-07-30T14:35:00+09:00',
    entryPrice: 132_300,
    avgExitPrice: 122_700,
    qty: 30,
    cost: 3_969_000,
    realizedPnl: -288_000,
    returnPct: -7.25,
    holdDays: 38,
    exitCascade: ['stop_loss_7pct'],
    closeCount: 1,
  },
  {
    cycleId: '195870-1720000000-BT-04',
    symbol: '195870',
    symbolName: '신화콘텍',
    sector: '소재·화학',
    openedAt: '2025-07-04T09:45:00+09:00',
    closedAt: '2025-12-12T15:00:00+09:00',
    entryPrice: 4_815,
    avgExitPrice: 7_990,
    qty: 1_200,
    cost: 5_778_000,
    realizedPnl: 3_810_000,
    returnPct: 65.93,
    holdDays: 161,
    exitCascade: ['tp_15pct', 'tp_30pct', 'tp_50pct', 'time_exit'],
    closeCount: 4,
  },
  {
    cycleId: '035720-1722000000-BT-05',
    symbol: '035720',
    symbolName: '카카오',
    sector: '인터넷·플랫폼',
    openedAt: '2025-07-27T10:10:00+09:00',
    closedAt: '2025-09-18T11:45:00+09:00',
    entryPrice: 51_800,
    avgExitPrice: 58_900,
    qty: 80,
    cost: 4_144_000,
    realizedPnl: 568_000,
    returnPct: 13.71,
    holdDays: 53,
    exitCascade: ['tp_10pct', 'ma20_exit'],
    closeCount: 2,
  },
  {
    cycleId: '042700-1724000000-BT-06',
    symbol: '042700',
    symbolName: '한미반도체',
    sector: '반도체장비',
    openedAt: '2025-08-19T13:20:00+09:00',
    closedAt: '2026-01-15T15:00:00+09:00',
    entryPrice: 89_500,
    avgExitPrice: 154_300,
    qty: 60,
    cost: 5_370_000,
    realizedPnl: 3_888_000,
    returnPct: 72.40,
    holdDays: 149,
    exitCascade: ['tp_15pct', 'tp_30pct', 'tp_50pct', 'ma60_exit'],
    closeCount: 3,
  },
  {
    cycleId: '003490-1725000000-BT-07',
    symbol: '003490',
    symbolName: '대한항공',
    sector: '운송·물류',
    openedAt: '2025-08-30T09:40:00+09:00',
    closedAt: '2025-11-10T15:00:00+09:00',
    entryPrice: 25_800,
    avgExitPrice: 38_200,
    qty: 200,
    cost: 5_160_000,
    realizedPnl: 2_480_000,
    returnPct: 48.06,
    holdDays: 72,
    exitCascade: ['tp_15pct', 'tp_30pct', 'tp_45pct'],
    closeCount: 3,
  },
  {
    cycleId: '034730-1726000000-BT-08',
    symbol: '034730',
    symbolName: 'SK',
    sector: '통신·소프트웨어',
    openedAt: '2025-09-11T10:00:00+09:00',
    closedAt: '2025-10-22T15:00:00+09:00',
    entryPrice: 175_000,
    avgExitPrice: 168_400,
    qty: 24,
    cost: 4_200_000,
    realizedPnl: -158_400,
    returnPct: -3.77,
    holdDays: 41,
    exitCascade: ['ma20_exit'],
    closeCount: 1,
  },
  {
    cycleId: '055550-1727000000-BT-09',
    symbol: '055550',
    symbolName: '신한지주',
    sector: '금융·은행',
    openedAt: '2025-09-22T14:15:00+09:00',
    closedAt: '2026-02-13T15:00:00+09:00',
    entryPrice: 52_400,
    avgExitPrice: 64_100,
    qty: 95,
    cost: 4_978_000,
    realizedPnl: 1_111_500,
    returnPct: 22.33,
    holdDays: 144,
    exitCascade: ['tp_10pct', 'tp_20pct', 'time_exit'],
    closeCount: 3,
  },
  {
    cycleId: '267260-1728000000-BT-10',
    symbol: '267260',
    symbolName: 'HD현대일렉트릭',
    sector: '전력·인프라',
    openedAt: '2025-10-08T13:00:00+09:00',
    closedAt: null,
    entryPrice: 184_500,
    avgExitPrice: null,
    qty: 32,
    cost: 5_904_000,
    realizedPnl: 0,
    returnPct: 0,
    holdDays: 142,
    exitCascade: [],
    closeCount: 0,
  },
];

export const MOCK_BACKTEST_TRADES: Record<string, BacktestTradesPage> = {
  __default__: {
    runId: '__default__',
    totalCycles: SAMPLE_TRADES.length,
    trades: SAMPLE_TRADES,
  },
  'bt-2026-05-01': {
    runId: 'bt-2026-05-01',
    totalCycles: SAMPLE_TRADES.length,
    trades: SAMPLE_TRADES,
  },
  'bt-2026-04-15': {
    runId: 'bt-2026-04-15',
    totalCycles: SAMPLE_TRADES.length,
    trades: SAMPLE_TRADES.map((t) => ({
      ...t,
      realizedPnl: Math.round(t.realizedPnl * 0.85),
      returnPct: +(t.returnPct * 0.85).toFixed(2),
    })),
  },
  'bt-2026-04-02': {
    runId: 'bt-2026-04-02',
    totalCycles: SAMPLE_TRADES.length,
    trades: SAMPLE_TRADES.map((t) => ({
      ...t,
      realizedPnl: Math.round(t.realizedPnl * 1.12),
      returnPct: +(t.returnPct * 1.12).toFixed(2),
    })),
  },
};

export const MOCK_BACKTEST_AUDIT: Record<string, BacktestAuditItem[]> = {
  __default__: [
    {
      occurredAt: '2026-04-30T15:14:00+09:00',
      symbol: '005930',
      eventCode: 'TRADING.SIGNAL.QUEUED',
      summary: 'admission ok',
    },
    {
      occurredAt: '2026-04-30T14:22:00+09:00',
      symbol: '000660',
      eventCode: 'TRADING.SIGNAL.DROPPED.LIQUIDITY',
      summary: 'bid_amount < 1억',
    },
    {
      occurredAt: '2026-04-29T09:08:00+09:00',
      symbol: '035720',
      eventCode: 'TRADING.ORDER.FILLED',
      summary: 'qty=120 avg=42500',
    },
  ],
};
